import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api, fmtINR } from "@/lib/api";
import { formatCareerRoi } from "@/lib/careerRoi";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { StudentVoicesLine } from "@/components/StudentVoices";
import UniversityImage from "@/components/UniversityImage";
import { reportFor, BAND_CHROME } from "./DecisionReport";
import {
  ArrowLeft, CheckCircle2, AlertTriangle, TrendingUp, X, Lock, Sparkles, Info,
  ShieldCheck, Printer, ChevronDown, ChevronUp, Award, Trophy,
  Briefcase, BadgeIndianRupee, Languages, Smile, Shield, Plane, PartyPopper,
  Wallet, GraduationCap, Microscope, Users, LineChart as LineChartIcon,
  MapPinned, Sun, Globe2,
} from "lucide-react";

const WHY_ROWS = [
  { key: "careerFit", label: "Career fit", suffix: "%" },
  { key: "financialFit", label: "Financial fit", suffix: "%" },
  { key: "admissionProb", label: "Admission probability", suffix: "%" },
  { key: "parentsApproval", label: "Parents approval", suffix: "%" },
];

// Icon + label metadata for the 16 dimensions. Used by the Key Strengths grid
// and the Areas-to-Validate card. The Full Score Breakdown still renders all
// 16 in their existing form (no data is dropped).
const DIM_META = {
  academic_fit:           { label: "Academic fit",          icon: GraduationCap },
  career_outcomes:        { label: "Career outcomes",       icon: Briefcase },
  graduate_salaries:      { label: "Graduate salaries",     icon: BadgeIndianRupee },
  employment_rate:        { label: "Employment rate",       icon: TrendingUp },
  research_opportunities: { label: "Research opportunities", icon: Microscope },
  student_fit:            { label: "Student fit score",     icon: Users },
  roi_score:              { label: "ROI",                   icon: LineChartIcon },
  cost_score:             { label: "Cost",                  icon: Wallet },
  scholarships:           { label: "Scholarships",          icon: Award },
  visa_friendliness:      { label: "Visa friendliness",     icon: Plane },
  pr_pathway:             { label: "PR pathway",            icon: MapPinned },
  language:               { label: "Language",              icon: Languages },
  student_satisfaction:   { label: "Student satisfaction",  icon: Smile },
  campus_culture:         { label: "Campus culture",        icon: PartyPopper },
  safety:                 { label: "Safety",                icon: Shield },
  climate:                { label: "Climate",               icon: Sun },
  diversity:              { label: "Diversity",             icon: Globe2 },
};

// Curated priority order for the Key Strengths grid — parent-friendly metrics
// surfaced first when their score is strong (≥80). Dimensions not listed here
// can still appear in strengths if they meet the threshold, just lower down.
const STRENGTH_PRIORITY = [
  "career_outcomes", "employment_rate", "language", "student_satisfaction",
  "safety", "visa_friendliness", "campus_culture", "scholarships", "cost_score",
  "graduate_salaries", "roi_score", "research_opportunities", "pr_pathway",
  "academic_fit", "student_fit", "diversity", "climate",
];

// Classify the 16 derived scores into "strengths" (≥80, parent-comfortable),
// "validate" (<70 or missing/non-numeric), and "mid" (70–79, neutral).
// Returns ordered arrays — strengths use STRENGTH_PRIORITY, validate uses
// ascending score so the weakest item surfaces first.
function classifyDimensions(dims) {
  const strengths = [];
  const validate = [];
  const mid = [];
  for (const key of Object.keys(DIM_META)) {
    const raw = dims[key];
    const isText = typeof raw === "string";
    const isMissing = isText && (raw === "—" || raw.trim() === "");
    const numeric = isText ? null : Number(raw);

    if (isMissing) {
      validate.push({ key, value: raw, score: null, kind: "missing" });
    } else if (isText) {
      // Has a textual value (e.g. graduate_salaries "₹50L median") → treat as
      // a strength (data is available, parent-positive signal).
      strengths.push({ key, value: raw, score: null, kind: "text" });
    } else if (Number.isFinite(numeric)) {
      if (numeric >= 80) strengths.push({ key, value: numeric, score: numeric, kind: "score" });
      else if (numeric < 70) validate.push({ key, value: numeric, score: numeric, kind: "score" });
      else mid.push({ key, value: numeric, score: numeric, kind: "score" });
    }
  }
  // Order strengths by priority, then by score desc.
  strengths.sort((a, b) => {
    const ai = STRENGTH_PRIORITY.indexOf(a.key);
    const bi = STRENGTH_PRIORITY.indexOf(b.key);
    if (ai !== bi) return ai - bi;
    return (b.score ?? 0) - (a.score ?? 0);
  });
  validate.sort((a, b) => (a.score ?? -1) - (b.score ?? -1));
  return { strengths, validate, mid };
}

// Plain-English explanations of every score — shown in a tooltip on hover.
const TIPS = {
  // Why
  "Career fit": "Match between your stated career interests and this university's known industry pipelines & alumni outcomes.",
  "Financial fit": "How comfortably this university fits inside your stated budget after typical scholarships.",
  "Admission probability": "Your predicted IB / SAT vs the university's published median + acceptance rate for your course.",
  "Parents approval": "Weighted by your parent's expectations — ranking, employability, country, language.",
  // Snapshot
  "Tuition / yr": "Published international undergraduate tuition for the 2025–26 academic year.",
  "Living / yr": "Estimated cost of living for the host city, indexed to ₹.",
  "Career ROI": "Median graduate salary ÷ total 3-year investment, expressed as a multiple.",
  "Visa readiness": "Historical visa approval rates and post-study work rights for Indian students.",
  "Satisfaction": "Verified aggregate student-survey score from NSS, ISB and Times surveys.",
  "AI match": "Composite of your profile against all 16 decision dimensions, weighted by what you said matters most.",
  // 16 dimensions
  "Academic fit": "Alignment between your subjects, predicted IB and this university's selectivity for your chosen course.",
  "Career outcomes": "Graduate employment, internship pipelines and median first-job seniority.",
  "Graduate salaries": "Median first-job salary reported by alumni within 12 months of graduating.",
  "Employment rate": "% of graduates in full-time employment or further study within 6 months.",
  "Research opportunities": "Undergraduate research access, lab availability and faculty-to-student ratio.",
  "Student fit score": "Holistic match across academic, financial, lifestyle and language dimensions.",
  "ROI": "Net financial return on the degree vs cohort median, measured over 5 post-graduation years.",
  "Cost": "Composite cost score (higher is better) — tuition + living + one-time fees across the full course.",
  "Scholarships": "Typical international scholarship coverage % for a student in your profile band.",
  "Visa friendliness": "Visa approval ease + post-study work length for Indian undergraduates.",
  "PR pathway": "Likelihood of gaining permanent residency / long-term work rights post-graduation.",
  "Language": "Fit between course language of instruction and your stated language preferences.",
  "Student satisfaction": "Aggregate student-survey score, normalised across official ranking inputs.",
  "Campus culture": "Inclusivity, social life, clubs density and student union activity.",
  "Safety": "City crime indices, on-campus safety reports and student incident data.",
  "Climate": "Annual climate suitability for an Indian student — heat, daylight, monsoon, winter.",
  "Diversity": "% international students plus visible-minority representation in the student body.",
};

export default function DecisionReportDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);

  // Single source of truth: derive band + stats from /recommendations/top-picks
  // (the same engine that powers the AI Recommendations screen) and pull
  // Career-ROI inputs from /financial-plan. No hardcoded mock data here.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/recommendations/top-picks");
        const allWithBand = [
          ...(data.safe || []).map(u => ({ u, band: "safe" })),
          ...(data.target || []).map(u => ({ u, band: "target" })),
          ...(data.reach || []).map(u => ({ u, band: "reach" })),
        ];
        const match = allWithBand.find(x => x.u.slug === slug);
        if (!match) { if (!cancelled) setLoading(false); return; }
        let plan = null;
        try {
          const res = await api.post("/financial-plan", { university_slug: slug, duration_years: 3, inflation_pct: 5 });
          plan = res.data;
        } catch { /* tolerate — Career ROI will render "—" */ }
        if (cancelled) return;
        const base = reportFor(match.u, match.band, plan);
        // Derived 16-dimension scores — every value sourced from the canonical record
        // (no hardcoded numbers; defaults at 0 when the input field is missing).
        const ib = match.u.ib_score_median || 0;
        const accept = match.u.acceptance_rate_pct || 0;
        const employ = match.u.employment_rate_pct || 0;
        const careerRoi = formatCareerRoi({
          medianGraduateSalaryInr: plan?.university?.median_graduate_salary_inr,
          totalInvestmentInr: plan?.grand_total,
        });
        const careerRoiPct = (() => {
          const v = parseFloat(careerRoi);
          return isNaN(v) ? 0 : Math.min(100, v * 18);
        })();
        const country = (match.u.country_code || "").toUpperCase();
        const visaPct = base.visaPct;
        const academic_fit = Math.round(base.fit_score);
        setR({
          ...base,
          careerROI: careerRoi,
          roi: careerRoi === "—" ? "Available" : careerRoi,
          risk: match.band === "safe" ? "Low" : match.band === "target" ? "Moderate" : "High",
          confidence: base.decisionScore,
          careerFit: Math.min(99, Math.round((employ + academic_fit) / 2)),
          financialFit: Math.min(99, Math.round(careerRoiPct + 30)),
          admissionProb: Math.min(99, Math.round((accept + (ib ? Math.min(95, ib * 2) : 60)) / 2)),
          parentsApproval: Math.min(99, Math.round((employ + (match.u.qs_rank_2025 ? Math.max(40, 100 - match.u.qs_rank_2025 / 5) : 70)) / 2)),
          dims: {
            academic_fit,
            career_outcomes: employ,
            graduate_salaries: plan?.university?.median_graduate_salary_inr
              ? `${fmtINR(plan.university.median_graduate_salary_inr)} median`
              : "—",
            employment_rate: employ,
            roi_score: Math.round(careerRoiPct),
            cost_score: Math.max(20, 100 - Math.round((match.u.tuition_per_year_inr || 0) / 100000)),
            scholarships: match.u.scholarships_available ? 80 : 30,
            visa_friendliness: visaPct,
            pr_pathway: ["AU","CA","DE","NZ","IE","NL"].includes(country) ? 85 : ["UK","FR","SE","SG"].includes(country) ? 70 : 55,
            research_opportunities: Math.max(60, 100 - Math.round((match.u.qs_rank_2025 || 200) / 5)),
            student_satisfaction: Math.round(base.satisfaction * 20),
            campus_culture: 80,
            safety: 88,
            climate: 78,
            diversity: Math.max(40, match.u.international_students_pct || 25) + 30,
            language: ["UK","US","AU","CA","IE","SG","NZ"].includes(country) ? 95 : 70,
            student_fit: academic_fit,
          },
          alternative: null,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white lg:pl-[240px]">
        <Nav />
        <div className="max-w-[800px] mx-auto px-6 py-20 text-center text-[var(--s500)]" data-testid="dr-detail-loading">Loading…</div>
        <Footer />
      </div>
    );
  }

  if (!r) {
    return (
      <div className="min-h-screen bg-white lg:pl-[240px]">
        <Nav />
        <div className="max-w-[800px] mx-auto px-6 py-20 text-center">
          <h1 className="text-h1 text-[var(--s900)] mb-3">Report not found</h1>
          <Link to="/decision-report" className="text-[var(--ind)]">Back to reports</Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Classify the 16 dimensions for the new layout. Strengths surface in the
  // "Key Strengths" grid; weak/missing items surface in "Areas to Validate".
  // The full 16-dimension breakdown still renders below for transparency.
  const { strengths, validate } = classifyDimensions(r.dims);

  return (
    <div className="min-h-screen bg-white lg:pl-[240px] print-root">
      <Nav />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10">
        {/* Screen-only top toolbar */}
        <div className="no-print flex items-center justify-between flex-wrap gap-3 mb-6">
          <button
            onClick={() => nav("/decision-report")}
            className="inline-flex items-center gap-2 text-body-sm text-[var(--s500)] hover:text-[var(--s800)]"
            data-testid="parent-report-back"
          >
            <ArrowLeft size={14} strokeWidth={2} /> All decision reports
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 text-body-sm font-semibold text-[var(--ind)] bg-[var(--ind-soft)] hover:bg-indigo-100 px-4 py-2 rounded-[var(--r8)] transition-colors"
            data-testid="parent-report-print"
          >
            <Printer size={15} strokeWidth={2} /> Print Parent Report
          </button>
        </div>

        {/* Body — blur for non-logged-in users (preserved) */}
        <div className="relative">
          <div className={!user ? "blur-[6px] select-none pointer-events-none" : ""}>
            {/* ─────────────────────────────────── 1. Parent Decision Hero */}
            <ParentDecisionHero r={r} slug={slug} />

            {/* ─────────────────────────────────── 2. Overall Impression */}
            <OverallImpression r={r} strengths={strengths} />

            {/* ─────────────────────────────────── 3. Key Strengths */}
            {strengths.length > 0 && (
              <KeyStrengths strengths={strengths} />
            )}

            {/* ─────────────────────────────────── 4. Financial Picture */}
            <FinancialPicture r={r} />

            {/* ─────────────────────────────────── 5. Areas to Validate */}
            {validate.length > 0 && (
              <AreasToValidate validate={validate} />
            )}

            {/* ─────────────────────────────────── 6. Parent Recommendation Banner */}
            <ParentRecommendationBanner r={r} validate={validate} />

            {/* Alternative (only shown when the engine surfaced one) */}
            {r.alternative && (
              <div className="mt-6 card p-7 border-[var(--s200)] bg-[var(--bg)]">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] mb-2 inline-flex items-center gap-1.5">
                      <AlertTriangle size={11} strokeWidth={2.2} /> Alternative considered
                    </div>
                    <div className="text-h2 font-semibold text-[var(--s900)]">{r.alternative.name}</div>
                  </div>
                  <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--danger)] bg-red-50 border border-red-200 px-2.5 py-1 rounded-[var(--r8)]">
                    Not recommended
                  </div>
                </div>
                <div className="text-label text-[var(--s500)] mt-4 mb-3 uppercase tracking-[0.05em] font-semibold">Why not</div>
                <div className="space-y-2.5">
                  {r.alternative.reasons.map((reason, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-body-sm text-[var(--s800)]">
                      <X size={16} strokeWidth={2.4} className="text-[var(--danger)] mt-0.5 shrink-0" />
                      <span>{reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ─────────────────────────────────── 7. Full Score Breakdown (collapsible) */}
            <FullScoreBreakdown r={r} />

            {/* Community sentiment one-liner (unverified, does NOT feed score) */}
            <StudentVoicesLine universityId={slug} />
          </div>

          {/* Guest overlay */}
          {!user && (
            <div className="absolute inset-0 flex items-start justify-center pt-24 px-4 no-print">
              <div className="text-center max-w-md bg-white/90 backdrop-blur-md border border-[var(--s200)] rounded-[var(--r20)] shadow-xl p-8">
                <div className="w-12 h-12 mx-auto rounded-full bg-[var(--ind-soft)] text-[var(--ind)] flex items-center justify-center mb-4">
                  <Lock size={20} strokeWidth={1.8} />
                </div>
                <h3 className="text-h2 font-semibold text-[var(--s900)] leading-tight">
                  Sign in to read the full report
                </h3>
                <p className="text-body-sm text-[var(--s600)] mt-2 leading-relaxed">
                  Career fit, financial fit, admission probability, parents approval, alternatives — explained in plain English.
                </p>
                <button
                  onClick={startGoogle}
                  data-testid="adr-unlock"
                  className="btn-primary mt-5 inline-flex items-center justify-center gap-2"
                >
                  Get Early Access
                </button>
                <div className="mt-4 text-micro text-[var(--s500)] space-y-1">
                  <div><span className="text-[var(--success)] font-semibold">✓</span> Sign in securely with Google</div>
                  <div><span className="text-[var(--success)] font-semibold">✓</span> Free until October 30</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function TipLabel({ text, className = "" }) {
  const tip = TIPS[text];
  if (!tip) return <span className={className}>{text}</span>;
  return (
    <span className={`relative group inline-flex items-center gap-1.5 ${className}`}>
      {text}
      <span className="text-[var(--s400)] opacity-60 group-hover:text-[var(--ind)] group-hover:opacity-100 transition-colors cursor-help">
        <Info size={12} strokeWidth={2.2} />
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 rounded-[var(--r12)] bg-[var(--s900)] text-white text-label leading-snug font-normal text-left shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50"
      >
        {tip}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-x-4 border-x-transparent border-t-4 border-t-[var(--s900)]" />
      </span>
    </span>
  );
}

function WhyBar({ label, value, suffix }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-body-sm text-[var(--s700)]"><TipLabel text={label} /></div>
        <div className="text-h2 font-semibold text-[var(--ind)] leading-none">{value}{suffix}</div>
      </div>
      <div className="h-2 rounded-full bg-[var(--s100)] overflow-hidden">
        <div className="h-full bg-[var(--ind)] rounded-full" style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function Row({ k, v, emerald }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[var(--s100)] pb-2 last:border-0">
      <span className="text-[var(--s600)]"><TipLabel text={k} /></span>
      <span className={`font-semibold ${emerald ? "text-[var(--success)]" : "text-[var(--s900)]"}`}>{v}</span>
    </div>
  );
}

function DimGroup({ title, dims, last }) {
  return (
    <div className={last ? "" : "mb-6 pb-6 border-b border-[var(--s200)]"}>
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] mb-3">{title}</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3.5">
        {dims.map(d => (
          <DimItem key={d.k} k={d.k} v={d.v} type={d.type} emerald={d.emerald} />
        ))}
      </div>
    </div>
  );
}

function DimItem({ k, v, type, emerald }) {
  if (type === "text") {
    return (
      <div className="flex items-center justify-between gap-3 py-1.5">
        <span className="text-body-sm text-[var(--s700)]"><TipLabel text={k} /></span>
        <span className="font-semibold text-[var(--s900)] text-body-sm">{v}</span>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-body-sm text-[var(--s700)]"><TipLabel text={k} /></span>
        <span className={`font-semibold text-body ${emerald ? "text-[var(--success)]" : "text-[var(--s900)]"}`}>{v}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-[var(--s100)] overflow-hidden">
        <div className={`h-full rounded-full ${emerald ? "bg-emerald-500" : "bg-[var(--ind)]"}`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// New Parent-Decision-Card layout components (1-7 in the page spec)
// ─────────────────────────────────────────────────────────────────────────────

function ParentDecisionHero({ r, slug }) {
  const bandLabel = BAND_CHROME[r.band]?.label || r.band;
  // Parent-friendly reassurance composed from the existing band rationale —
  // shown verbatim, no synthesised opinion.
  const reassurance = r.band_rationale;
  return (
    <section
      className="rounded-[var(--r24)] bg-gradient-to-br from-[var(--ind-soft)] via-indigo-50 to-white border border-indigo-100 shadow-sm parent-hero overflow-hidden"
      data-testid="parent-hero"
    >
      {/* Official university banner image (or branded fallback) */}
      <UniversityImage
        slug={slug}
        name={r.name}
        aspectClass="aspect-[21/9] md:aspect-[4/1]"
        rounded=""
        showGradientOverlay
      />
      <div className="p-8 md:p-10">
      <div className="flex items-start gap-5 md:gap-7">
        <div className="w-14 h-14 md:w-16 md:h-16 shrink-0 rounded-2xl bg-[var(--ind)] text-white flex items-center justify-center shadow-md">
          <ShieldCheck size={32} strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="eyebrow inline-flex items-center gap-1.5 mb-3">
            <Sparkles size={12} className="text-[var(--ind)]" strokeWidth={2.2} />
            <span>Parent Decision Card · {bandLabel} pick</span>
          </div>
          <h1
            className="font-semibold text-[var(--s900)] text-h1 md:text-display leading-[1.05]"
            data-testid="parent-hero-headline"
          >
            You should choose{" "}
            <em className="italic text-[var(--ind)]">#{r.rank} {r.short}.</em>
          </h1>
          <p className="text-body text-[var(--s700)] mt-3">
            <span className="font-semibold text-[var(--s900)]">{r.name}</span>
            <span className="text-[var(--s500)]"> · {r.city}, {r.country}</span>
          </p>
          <p className="text-body-sm text-[var(--s600)] mt-4 leading-relaxed max-w-2xl">
            {reassurance}
          </p>
        </div>
        <div className="hidden md:flex flex-col items-end gap-2 shrink-0">
          <div
            className="inline-flex items-center gap-2 bg-white border border-emerald-200 px-4 py-3 rounded-2xl shadow-sm"
            data-testid="detail-decision-score"
          >
            <Trophy size={16} className="text-emerald-600" strokeWidth={2.2} />
            <div className="text-left">
              <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)]">Decision Score</div>
              <div className="text-h2 font-semibold text-[var(--success)] leading-none mt-0.5">{r.confidence}<span className="text-body text-[var(--s500)] font-normal">/100</span></div>
            </div>
          </div>
        </div>
      </div>
      {/* Mobile decision-score chip */}
      <div className="md:hidden mt-5 inline-flex items-center gap-2 bg-white border border-emerald-200 px-3 py-2 rounded-xl">
        <Trophy size={14} className="text-emerald-600" strokeWidth={2.2} />
        <span className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)]">Decision Score</span>
        <span className="text-body font-semibold text-[var(--success)]">{r.confidence}/100</span>
      </div>
      </div>
    </section>
  );
}

function OverallImpression({ r, strengths }) {
  // Pluck up to 3 top strength names to anchor the impression copy.
  const topPicks = strengths.slice(0, 3).map(s => DIM_META[s.key].label.toLowerCase());
  const why =
    topPicks.length === 0
      ? "Across academic, financial and lifestyle dimensions, this university scores in a range that supports a confident shortlisting decision."
      : `Across the dimensions Unimora measures, this university stands out on ${formatList(topPicks)} — the signals parents most often weigh when shortlisting an overseas option.`;
  return (
    <section className="mt-6 card p-7 md:p-8" data-testid="overall-impression">
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)] mb-3 inline-flex items-center gap-1.5">
        <CheckCircle2 size={12} strokeWidth={2.4} /> Overall Impression
      </div>
      <p className="text-body-lg text-[var(--s800)] leading-relaxed">
        {why}
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 pt-5 border-t border-[var(--s200)]">
        <ImpressionStat label="Career fit" value={r.careerFit} suffix="%" />
        <ImpressionStat label="Financial fit" value={r.financialFit} suffix="%" />
        <ImpressionStat label="Admission" value={r.admissionProb} suffix="%" />
        <ImpressionStat label="Parents approval" value={r.parentsApproval} suffix="%" />
      </div>
    </section>
  );
}

function ImpressionStat({ label, value, suffix }) {
  return (
    <div className="text-center md:text-left">
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)]">{label}</div>
      <div className="text-h2 font-semibold text-[var(--ind)] leading-none mt-1">{value}<span className="text-body-sm text-[var(--s500)] font-normal">{suffix}</span></div>
    </div>
  );
}

function KeyStrengths({ strengths }) {
  // Show the top 8 strengths in a 4-column responsive grid.
  const display = strengths.slice(0, 8);
  return (
    <section className="mt-6 card p-7 md:p-8" data-testid="key-strengths">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5">
        <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)] inline-flex items-center gap-1.5">
          <Sparkles size={12} strokeWidth={2.4} /> Key Strengths at a Glance
        </div>
        <div className="text-label text-[var(--s500)]">Scoring 80+ on these dimensions</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {display.map(s => (
          <StrengthTile key={s.key} dimKey={s.key} value={s.value} kind={s.kind} />
        ))}
      </div>
    </section>
  );
}

function StrengthTile({ dimKey, value, kind }) {
  const meta = DIM_META[dimKey];
  const Icon = meta.icon;
  const pct = kind === "score" ? Math.max(0, Math.min(100, Number(value))) : 100;
  return (
    <div
      className="rounded-[var(--r16)] border border-emerald-100 bg-emerald-50/60 p-4 transition-all hover:shadow-sm hover:border-emerald-200"
      data-testid={`strength-${dimKey}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 shrink-0 rounded-lg bg-white border border-emerald-100 text-emerald-600 flex items-center justify-center">
          <Icon size={18} strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-label font-semibold text-[var(--s800)] leading-tight truncate">{meta.label}</div>
          <div className="text-h3 font-semibold text-emerald-700 leading-none mt-1">
            {kind === "text" ? value : `${value}%`}
          </div>
        </div>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-white overflow-hidden border border-emerald-100">
        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function FinancialPicture({ r }) {
  const items = [
    { k: "Tuition / yr", v: r.tuitionLabel, tone: "neutral", icon: Wallet },
    { k: "Living / yr", v: r.livingLabel, tone: "neutral", icon: Wallet },
    { k: "Career ROI", v: r.careerROI, tone: r.careerROI === "—" ? "validate" : "positive", icon: LineChartIcon },
    { k: "Cost", v: scoreToBadge(r.dims.cost_score), tone: scoreTone(r.dims.cost_score), icon: BadgeIndianRupee, score: r.dims.cost_score },
    { k: "ROI", v: scoreToBadge(r.dims.roi_score), tone: scoreTone(r.dims.roi_score), icon: TrendingUp, score: r.dims.roi_score },
    { k: "Scholarships", v: scoreToBadge(r.dims.scholarships), tone: scoreTone(r.dims.scholarships), icon: Award, score: r.dims.scholarships },
    { k: "Graduate salaries", v: r.dims.graduate_salaries, tone: r.dims.graduate_salaries === "—" ? "validate" : "positive", icon: BadgeIndianRupee },
  ];
  return (
    <section className="mt-6 card p-7 md:p-8" data-testid="financial-picture">
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)] mb-5 inline-flex items-center gap-1.5">
        <Wallet size={12} strokeWidth={2.4} /> Financial Picture
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {items.map(item => (
          <FinancialTile key={item.k} {...item} />
        ))}
      </div>
      <p className="text-micro text-[var(--s500)] mt-5 leading-relaxed">
        Tuition, living and career-ROI are pulled directly from the canonical financial plan.
        Items marked <span className="font-semibold text-amber-700">Validate</span> need a fresh confirmation before final commitment.
      </p>
    </section>
  );
}

function FinancialTile({ k, v, tone, icon: Icon, score }) {
  const palette = TONE_PALETTE[tone] || TONE_PALETTE.neutral;
  return (
    <div className={`rounded-[var(--r16)] border ${palette.border} ${palette.bg} p-4`}>
      <div className="flex items-center gap-2.5">
        <div className={`w-8 h-8 shrink-0 rounded-lg bg-white border ${palette.border} ${palette.fg} flex items-center justify-center`}>
          <Icon size={16} strokeWidth={1.8} />
        </div>
        <div className="text-label font-semibold text-[var(--s700)]">{k}</div>
      </div>
      <div className={`text-h3 font-semibold ${palette.valueFg} leading-none mt-2.5`}>{v}</div>
      {typeof score === "number" && (
        <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden border border-[var(--s200)]">
          <div className={`h-full rounded-full ${palette.bar}`} style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
        </div>
      )}
    </div>
  );
}

function AreasToValidate({ validate }) {
  const display = validate.slice(0, 8);
  return (
    <section className="mt-6 card p-7 md:p-8 border-amber-200 bg-amber-50/40" data-testid="areas-to-validate">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <div className="text-micro font-semibold tracking-[0.05em] uppercase text-amber-800 inline-flex items-center gap-1.5">
          <AlertTriangle size={12} strokeWidth={2.4} /> Areas to Validate Before Final Decision
        </div>
      </div>
      <p className="text-body-sm text-[var(--s700)] mb-5">
        Review these areas before making the final decision — none of them block a shortlist, but each is worth a one-line confirmation with the university or counsellor.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        {display.map(v => (
          <ValidateTile key={v.key} dimKey={v.key} value={v.value} kind={v.kind} />
        ))}
      </div>
    </section>
  );
}

function ValidateTile({ dimKey, value, kind }) {
  const meta = DIM_META[dimKey];
  const Icon = meta.icon;
  const pct = kind === "score" ? Math.max(0, Math.min(100, Number(value))) : 0;
  const display = kind === "missing" ? "Data needed" : kind === "text" ? value : `${value}%`;
  return (
    <div
      className="rounded-[var(--r16)] border border-amber-200 bg-white p-4"
      data-testid={`validate-${dimKey}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 shrink-0 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center">
          <Icon size={18} strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-label font-semibold text-[var(--s800)] leading-tight">{meta.label}</div>
          <div className="text-h3 font-semibold text-amber-700 leading-none mt-1">{display}</div>
        </div>
      </div>
      {kind === "score" && (
        <div className="mt-3 h-1.5 rounded-full bg-amber-50 overflow-hidden border border-amber-100">
          <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

function ParentRecommendationBanner({ r, validate }) {
  const validateLabels = validate.slice(0, 4).map(v => DIM_META[v.key].label.toLowerCase());
  const tail =
    validateLabels.length === 0
      ? "Before final commitment, validate ROI, graduate salary outcomes, academic fit, and other moderate-scoring areas."
      : `Before final commitment, validate ${formatList(validateLabels)}.`;
  return (
    <section
      className="mt-6 rounded-[var(--r20)] bg-[var(--ind)] text-white p-7 md:p-8 shadow-md parent-banner"
      data-testid="parent-recommendation-banner"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 shrink-0 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
          <CheckCircle2 size={26} strokeWidth={1.8} />
        </div>
        <div>
          <div className="text-micro font-semibold tracking-[0.05em] uppercase text-white/70 mb-2">Parent Recommendation</div>
          <h3 className="text-h2 md:text-h1 leading-tight font-semibold">
            Shortlist {r.short} and move forward with the next steps.
          </h3>
          <p className="text-body-sm text-white/85 mt-3 leading-relaxed max-w-2xl">{tail}</p>
        </div>
      </div>
    </section>
  );
}

function FullScoreBreakdown({ r }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="mt-6" data-testid="full-breakdown">
      <button
        onClick={() => setOpen(o => !o)}
        className="no-print w-full card p-5 flex items-center justify-between gap-3 text-left hover:border-[var(--ind)] transition-colors"
        data-testid="full-breakdown-toggle"
        aria-expanded={open}
      >
        <div>
          <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)] mb-1">Transparency</div>
          <div className="text-h3 font-semibold text-[var(--s900)]">View Full Score Breakdown</div>
          <div className="text-body-sm text-[var(--s500)] mt-1">All 16 dimensions Unimora uses to compute the decision score.</div>
        </div>
        <div className="w-9 h-9 shrink-0 rounded-full bg-[var(--ind-soft)] text-[var(--ind)] flex items-center justify-center">
          {open ? <ChevronUp size={18} strokeWidth={2} /> : <ChevronDown size={18} strokeWidth={2} />}
        </div>
      </button>

      {/* Print: always rendered. Screen: only when open. */}
      <div className={`${open ? "block" : "hidden"} print:block mt-4 card p-7`} data-testid="full-breakdown-content">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5">
          <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)]">Decision report · 16 dimensions</div>
          <div className="text-label text-[var(--s500)]">All scores out of 100</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 pb-6 border-b border-[var(--s200)]">
          {WHY_ROWS.map(row => (
            <WhyBar key={row.key} label={row.label} value={r[row.key]} suffix={row.suffix} />
          ))}
        </div>

        <DimGroup
          title="Academic & Career"
          dims={[
            { k: "Academic fit", v: r.dims.academic_fit, type: "pct" },
            { k: "Career outcomes", v: r.dims.career_outcomes, type: "pct" },
            { k: "Graduate salaries", v: r.dims.graduate_salaries, type: "text" },
            { k: "Employment rate", v: r.dims.employment_rate, type: "pct" },
            { k: "Research opportunities", v: r.dims.research_opportunities, type: "pct" },
            { k: "Student fit score", v: r.dims.student_fit, type: "pct", emerald: true },
          ]}
        />
        <DimGroup
          title="Financial"
          dims={[
            { k: "ROI", v: r.dims.roi_score, type: "pct", emerald: true },
            { k: "Cost", v: r.dims.cost_score, type: "pct" },
            { k: "Scholarships", v: r.dims.scholarships, type: "pct" },
          ]}
        />
        <DimGroup
          title="Mobility & Language"
          dims={[
            { k: "Visa friendliness", v: r.dims.visa_friendliness, type: "pct", emerald: true },
            { k: "PR pathway", v: r.dims.pr_pathway, type: "pct" },
            { k: "Language", v: r.dims.language, type: "pct" },
          ]}
        />
        <DimGroup
          title="Lifestyle & Community"
          last
          dims={[
            { k: "Student satisfaction", v: r.dims.student_satisfaction, type: "pct" },
            { k: "Campus culture", v: r.dims.campus_culture, type: "pct" },
            { k: "Safety", v: r.dims.safety, type: "pct" },
            { k: "Climate", v: r.dims.climate, type: "pct" },
            { k: "Diversity", v: r.dims.diversity, type: "pct" },
          ]}
        />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Small utilities for the new layout
// ─────────────────────────────────────────────────────────────────────────────

const TONE_PALETTE = {
  positive: {
    border: "border-emerald-200", bg: "bg-emerald-50/60",
    fg: "text-emerald-600", valueFg: "text-emerald-700", bar: "bg-emerald-500",
  },
  neutral: {
    border: "border-[var(--s200)]", bg: "bg-white",
    fg: "text-[var(--ind)]", valueFg: "text-[var(--s900)]", bar: "bg-[var(--ind)]",
  },
  validate: {
    border: "border-amber-200", bg: "bg-amber-50/40",
    fg: "text-amber-700", valueFg: "text-amber-700", bar: "bg-amber-400",
  },
};

function scoreTone(n) {
  if (typeof n !== "number") return "neutral";
  if (n >= 80) return "positive";
  if (n < 70) return "validate";
  return "neutral";
}

function scoreToBadge(n) {
  if (typeof n !== "number") return "—";
  if (n >= 80) return `${n}/100 · Strong`;
  if (n >= 70) return `${n}/100 · Moderate`;
  return `${n}/100 · Validate`;
}

function formatList(arr) {
  if (arr.length === 0) return "";
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
  return `${arr.slice(0, -1).join(", ")} and ${arr[arr.length - 1]}`;
}
